import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Badge, Box, Button, Icon, IconButton, Spinner, Stack, Text, Tooltip } from "@chakra-ui/react";
import { MdOpenInNew, MdPictureAsPdf } from "react-icons/md";
import {
    INVOICE_STATUS_LABELS,
    INVOICE_STATUS_COLORS,
    INVOICES_PAGE_SIZE,
} from "./subscriptionsConstants";
import { DEFAULT_CHIP_COLOR } from "./subscriptionsConstants";

const rowProps = {
    p: 2.5,
    borderRadius: "6px",
    border: "1px solid rgba(255, 255, 255, 0.06)",
    background: "rgba(255, 255, 255, 0.02)",
};

const ZERO_DECIMAL_CURRENCIES = new Set(["mga", "jpy", "krw", "vnd", "clp", "isk", "huf"]);

/**
 * Normalizes the invoices payload to { items, hasMore, lastId }.
 * Supports raw arrays, the admin { items, has_more } envelope, and
 * Stripe-style { data, has_more } / { invoices } envelopes.
 * @param {*} data - Decrypted API payload
 * @returns {{ items: Array, hasMore: boolean, lastId: string|null }}
 */
function normalizeInvoices(data) {
    const candidates = [data, data?.items, data?.data, data?.invoices];
    const items = candidates.find(Array.isArray) || [];
    const hasMore = Boolean(data?.has_more);
    const lastId = items.length ? items[items.length - 1]?.id : null;
    return { items, hasMore, lastId };
}

/**
 * Stripe stores `created` as a unix timestamp; local KantoBiz invoices
 * (Orange Money prepaid) return it as an ISO string instead.
 * @param {Object} invoice - Invoice object
 * @returns {string}
 */
function formatInvoiceDate(invoice) {
    const created = invoice?.created;
    if (typeof created === "number") return dayjs.unix(created).format("DD/MM/YYYY");
    const iso = typeof created === "string" ? created : invoice?.created_at;
    if (!iso) return "—";
    const parsed = dayjs(iso);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
}

/**
 * Formats the invoice amount with its currency code.
 * Raw Stripe invoices (unix `created`) store amounts in the smallest currency
 * unit, so they are divided by 100 except for zero-decimal currencies; local
 * KantoBiz invoices (ISO `created`) already use the main unit.
 * @param {Object} invoice - Invoice object (used to detect the contract)
 * @param {number|null|undefined} amount - Amount as returned by the API
 * @returns {string}
 */
function formatInvoiceAmount(invoice, amount) {
    if (amount === null || amount === undefined) return "—";
    const code = (invoice?.currency || "").toLowerCase();
    const isStripeRaw = typeof invoice?.created === "number";
    const value = isStripeRaw && !ZERO_DECIMAL_CURRENCIES.has(code) ? amount / 100 : Number(amount);
    const text = ZERO_DECIMAL_CURRENCIES.has(code) ? String(Math.round(value)) : value.toFixed(2);
    return `${text} ${code.toUpperCase()}`.trim();
}

/**
 * Fetches the first page or appends the next page of invoices via cursor.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function fetchPage({ subscriptionId, cursor, append, signal, setItems, setHasMore, setLastId, setError, setLoading }) {
    setLoading(true);
    if (!append) setError(null);
    try {
        const url = `/admin/subscriptions/${subscriptionId}/invoices?limit=${INVOICES_PAGE_SIZE}` +
            (cursor ? `&starting_after=${encodeURIComponent(cursor)}` : "");
        const { data } = await axios.get(url);
        if (signal.aborted) return;
        const { items, hasMore, lastId } = normalizeInvoices(data);
        setItems((prev) => append ? [...prev, ...items] : items);
        setHasMore(hasMore);
        setLastId(lastId);
    } catch (err) {
        if (!signal.aborted) setError(err?.response?.data || "Impossible de charger les factures");
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * State and effects for the invoices list, including cursor pagination.
 * @param {string|number} subscriptionId - Target subscription ID
 * @returns {Object} state + handlers
 */
function useInvoices(subscriptionId) {
    const [items, setItems] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [lastId, setLastId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback((cursor, append, signal) => fetchPage({
        subscriptionId, cursor, append, signal, setItems, setHasMore, setLastId, setError, setLoading,
    }), [subscriptionId]);

    useEffect(() => {
        const controller = new AbortController();
        setItems([]); setHasMore(false); setLastId(null);
        if (subscriptionId) load(null, false, controller.signal);
        return () => controller.abort();
    }, [subscriptionId, load]);

    const loadMore = () => {
        const controller = new AbortController();
        load(lastId, true, controller.signal);
    };
    return { items, hasMore, loading, error, loadMore };
}

/**
 * Renders the colored status chip for an invoice.
 * @param {Object} props
 * @param {string} props.status - Invoice status key
 * @returns {React.ReactElement}
 */
function InvoiceStatusChip({ status }) {
    const scheme = INVOICE_STATUS_COLORS[status] || DEFAULT_CHIP_COLOR;
    const label = INVOICE_STATUS_LABELS[status] || status || "—";
    return (
        <Badge background={scheme.bg} color={scheme.color} border={`1px solid ${scheme.border}`}
            fontWeight={600} fontSize="0.68rem" px={2} borderRadius="full" textTransform="none">
            {label}
        </Badge>
    );
}

/**
 * Renders the action icons (open in Stripe, download PDF) for an invoice.
 * @param {Object} props
 * @param {Object} props.invoice - Invoice object
 * @returns {React.ReactElement}
 */
function InvoiceActions({ invoice }) {
    return (
        <Stack direction="row" spacing={1}>
            {invoice?.hosted_invoice_url && (
                <Tooltip label="Ouvrir la facture" hasArrow>
                    <IconButton as="a" aria-label="Ouvrir la facture" size="sm" variant="ghost"
                        href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                        color="rgba(255, 255, 255, 0.7)" icon={<Icon as={MdOpenInNew} boxSize="16px" />} />
                </Tooltip>
            )}
            {invoice?.invoice_pdf && (
                <Tooltip label="Télécharger le PDF" hasArrow>
                    <IconButton as="a" aria-label="Télécharger le PDF" size="sm" variant="ghost"
                        href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer"
                        color="rgba(255, 255, 255, 0.7)" icon={<Icon as={MdPictureAsPdf} boxSize="16px" />} />
                </Tooltip>
            )}
        </Stack>
    );
}

/**
 * Renders a single invoice row.
 * @param {Object} props
 * @param {Object} props.invoice - Invoice object
 * @returns {React.ReactElement}
 */
function InvoiceRow({ invoice }) {
    const amount = invoice?.amount_paid != null ? invoice.amount_paid : invoice?.amount_due;
    return (
        <Box {...rowProps}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Stack spacing={0.5} minW={0} flex={1}>
                    <Text color="rgba(255, 255, 255, 0.95)" fontSize="0.82rem" fontWeight={600} fontFamily="monospace">
                        {invoice?.number || invoice?.id || "—"}
                    </Text>
                    <Text color="rgba(255, 255, 255, 0.55)" fontSize="0.7rem">
                        {formatInvoiceDate(invoice)} · {formatInvoiceAmount(invoice, amount)}
                    </Text>
                </Stack>
                <InvoiceStatusChip status={invoice?.status} />
                <InvoiceActions invoice={invoice} />
            </Stack>
        </Box>
    );
}

/**
 * Invoices sub-tab. Cursor-paginated via Stripe `starting_after`.
 * @param {Object} props
 * @param {string|number} props.subscriptionId - Target subscription ID
 * @returns {React.ReactElement}
 */
function InvoicesList({ subscriptionId }) {
    const { items, hasMore, loading, error, loadMore } = useInvoices(subscriptionId);

    if (loading && items.length === 0) {
        return <Box display="flex" justifyContent="center" py={3}><Spinner size="md" color="#17dcb8" /></Box>;
    }
    if (error) return <Text color="rgba(229, 115, 115, 1)" fontSize="0.85rem">{typeof error === "string" ? error : "Erreur"}</Text>;
    if (items.length === 0) return <Text color="rgba(255, 255, 255, 0.5)" fontSize="0.85rem">Aucune facture</Text>;

    return (
        <Stack spacing={2}>
            {items.map((inv) => <InvoiceRow key={inv?.id} invoice={inv} />)}
            {hasMore && (
                <Button onClick={loadMore} isDisabled={loading} variant="ghost" alignSelf="center" color="#17dcb8" fontSize="0.8rem">
                    {loading ? "Chargement..." : "Charger plus"}
                </Button>
            )}
        </Stack>
    );
}

export default InvoicesList;
